import React, { Children, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Center, Column } from "@/shared/ui/Blocks";
import { IconButton } from "../IconButton";
import { cn } from "@/shared/utils/shadcn";

interface ToggleableSectionProps {
  defaultOpen?: boolean;
  keepOpen?: boolean;
  triggerLabel?: string;
  trigger?: ({
    isOpen,
    onClick,
  }: {
    isOpen?: boolean;
    onClick: () => void;
  }) => React.ReactElement;
  children: React.ReactNode;
  className?: string;
}

const containerVariants = {
  open: {
    opacity: 1,
    height: "auto",
    transition: {
      staggerChildren: 0.08,
      when: "beforeChildren",
      height: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
      opacity: { duration: 0.15, ease: "easeInOut" },
    },
  },
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      staggerChildren: 0,
      when: "afterChildren",
      height: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
      opacity: { duration: 0.15, ease: "easeInOut" },
    },
  },
};

const childVariants = {
  open: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  closed: { opacity: 0, y: 10, transition: { duration: 0.1 } },
};

function ToggleableSection(props: ToggleableSectionProps) {
  const {
    defaultOpen = false,
    keepOpen = false,
    trigger,
    children,
    triggerLabel,
    className,
  } = props;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const handleSetOpenState = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const TriggerComponent = useCallback(() => {
    if (trigger) {
      return trigger({ isOpen, onClick: handleSetOpenState });
    }

    return (
      <IconButton
        icon="ChevronDown"
        iconProps={{
          className: cn("ml-2 transition-all", isOpen ? "rotate-180" : ""),
        }}
        label={triggerLabel || "See more"}
        onClick={handleSetOpenState}
        className="flex-row-reverse shadow-none print:hidden"
        color="black"
        variant="outline"
        size="md"
      />
    );
  }, [isOpen, trigger, triggerLabel, handleSetOpenState]);

  // Only render children when open or animating
  return (
    <Column className={className}>
      {!keepOpen && (
        <Center>
          <TriggerComponent />
        </Center>
      )}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="toggleable-section"
            initial="closed"
            animate="open"
            exit="closed"
            variants={containerVariants}
            style={{ overflow: "hidden" }}
          >
            <Column>
              {Children.toArray(children).map((child, idx) => (
                <motion.div
                  key={idx}
                  variants={childVariants}
                  style={{ width: "100%" }}
                >
                  {child}
                </motion.div>
              ))}
            </Column>
          </motion.div>
        )}
      </AnimatePresence>
    </Column>
  );
}

export { ToggleableSection };
